// Perspective ("four-corner") crop helpers — pure Canvas, no dependencies.

export const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// Draw image rotated by 0/90/180/270 onto a canvas (optionally downscaled so the longest side <= maxSide)
export const rotatedCanvas = (img, rotation, maxSide = Infinity) => {
  const swap = rotation % 180 !== 0;
  const sw = img.naturalWidth, sh = img.naturalHeight;
  const k = Math.min(1, maxSide / Math.max(sw, sh));
  const w = Math.round((swap ? sh : sw) * k), h = Math.round((swap ? sw : sh) * k);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.translate(w / 2, h / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, (-sw * k) / 2, (-sh * k) / 2, sw * k, sh * k);
  return c;
};

// Solve 8x8 system for homography mapping unit rect (dst) -> quad (src)
const solveHomography = (dst, src) => {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = dst[i], [u, v] = src[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  const n = 8;
  for (let i = 0; i < n; i++) {
    let p = i;
    for (let r = i + 1; r < n; r++) if (Math.abs(A[r][i]) > Math.abs(A[p][i])) p = r;
    [A[i], A[p]] = [A[p], A[i]]; [b[i], b[p]] = [b[p], b[i]];
    for (let r = i + 1; r < n; r++) {
      const f = A[r][i] / A[i][i];
      for (let c = i; c < n; c++) A[r][c] -= f * A[i][c];
      b[r] -= f * b[i];
    }
  }
  const h = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let s = b[i];
    for (let c = i + 1; c < n; c++) s -= A[i][c] * h[c];
    h[i] = s / A[i][i];
  }
  return [...h, 1];
};

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// points: [TL, TR, BR, BL] in source canvas pixels. Returns a canvas with the straightened area.
export const warpQuad = (srcCanvas, points, maxSide = 1600) => {
  const [tl, tr, br, bl] = points;
  let w = Math.max(dist(tl, tr), dist(bl, br));
  let h = Math.max(dist(tl, bl), dist(tr, br));
  const k = Math.min(1, maxSide / Math.max(w, h));
  w = Math.max(1, Math.round(w * k)); h = Math.max(1, Math.round(h * k));
  const out = document.createElement("canvas");
  out.width = w; out.height = h;
  const octx = out.getContext("2d");
  const axisAligned = tl[1] === tr[1] && bl[1] === br[1] && tl[0] === bl[0] && tr[0] === br[0];
  if (axisAligned) {
    octx.drawImage(srcCanvas, tl[0], tl[1], tr[0] - tl[0], bl[1] - tl[1], 0, 0, w, h);
    return out;
  }
  const H = solveHomography([[0, 0], [w, 0], [w, h], [0, h]], points);
  const sctx = srcCanvas.getContext("2d");
  const S = sctx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
  const sd = S.data, sw = S.width, sh = S.height;
  const O = octx.createImageData(w, h), od = O.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const d = H[6] * x + H[7] * y + H[8];
      const u = (H[0] * x + H[1] * y + H[2]) / d, v = (H[3] * x + H[4] * y + H[5]) / d;
      const x0 = Math.floor(u), y0 = Math.floor(v);
      if (x0 < 0 || y0 < 0 || x0 >= sw - 1 || y0 >= sh - 1) continue;
      const fx = u - x0, fy = v - y0;
      const i00 = (y0 * sw + x0) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4;
      const o = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        od[o + c] = (sd[i00 + c] * (1 - fx) + sd[i10 + c] * fx) * (1 - fy) + (sd[i01 + c] * (1 - fx) + sd[i11 + c] * fx) * fy;
      }
      od[o + 3] = 255;
    }
  }
  octx.putImageData(O, 0, 0);
  return out;
};
